import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from './Page';
import { FaChartLine, FaCookie, FaCookieBite } from 'react-icons/fa';

export const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('what');

  const sections = [
    { id: 'what', title: t('cookiePolicy.sections.what.title') },
    { id: 'how', title: t('cookiePolicy.sections.how.title') },
    { id: 'types', title: t('cookiePolicy.sections.types.title') },
    { id: 'managing', title: t('cookiePolicy.sections.managing.title') },
    { id: 'changes', title: t('cookiePolicy.sections.changes.title') },
    { id: 'contact', title: t('cookiePolicy.sections.contact.title') },
  ];

  return (
    <Page>
      <div className="max-w-5xl mx-auto px-6 py-16 mt-12 text-white">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-center mb-3 text-stroke">
            <FaCookieBite className="inline-block mr-4" />
            {t('cookiePolicy.title')}
          </h1>
          <p className="text-gray-200">{t('cookiePolicy.lastUpdated')}</p>
        </div>

        {/* Content Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-9">
          {/* Left Sidebar Navigation */}
          <div className="md:col-span-1 mt-10">
            <div className="bg-transparent backdrop-blur-sm rounded-lg border border-gray-200 p-4 sticky top-24">
              <h2 className="font-bold text-lg mb-4 text-white">
                {t('cookiePolicy.contents')}
              </h2>
              <nav>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                          activeSection === section.id
                            ? 'bg-purple-800/30 text-white font-medium backdrop-blur-sm border border-white/20'
                            : 'hover:bg-white/10 text-white backdrop-blur-sm'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="md:col-span-2">
            <div className="bg-transparent rounded-lg p-8">
              {/* What Are Cookies */}
              <section
                id="what"
                className={activeSection === 'what' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.what.title')}
                </h2>
                <div className="bg-blue-50/30 backdrop-blur-sm border-l-4 border-blue-500 p-4 mb-6">
                  <p className="text-white">
                    {t('cookiePolicy.sections.what.description')}
                  </p>
                </div>
              </section>

              {/* How We Use Cookies */}
              <section
                id="how"
                className={activeSection === 'how' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.how.title')}
                </h2>
                <p className="text-white mb-6">
                  {t('cookiePolicy.sections.how.description')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50/30 backdrop-blur-sm p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                    <h3 className="font-semibold text-white mb-2">
                      {t('cookiePolicy.sections.how.improvingExperience')}
                    </h3>
                    <p className="text-gray-200 text-sm">
                      {t('cookiePolicy.sections.how.improvingExperienceDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50/30 backdrop-blur-sm p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                    <h3 className="font-semibold text-white mb-2">
                      {t('cookiePolicy.sections.how.performance')}
                    </h3>
                    <p className="text-gray-200 text-sm">
                      {t('cookiePolicy.sections.how.performanceDesc')}
                    </p>
                  </div>
                </div>

                {/* New section about our tools */}
                <div className="mt-6 p-4 bg-gray-50/20 backdrop-blur-sm rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-white mb-2">
                    {t('cookiePolicy.sections.how.toolsWeUse')}
                  </h3>
                  <p className="text-gray-200 text-sm mb-3">
                    {t('cookiePolicy.sections.how.toolsWeUseDesc')}
                  </p>
                  <ul className="list-disc list-inside text-gray-200 text-sm space-y-2">
                    <li>
                      <span className="font-medium text-purple-300">
                        CookieYes
                      </span>{' '}
                      - {t('cookiePolicy.sections.how.cookieYesDesc')}
                    </li>
                    <li>
                      <span className="font-medium text-purple-300">
                        Google Analytics
                      </span>{' '}
                      - {t('cookiePolicy.sections.how.googleAnalyticsDesc')}
                    </li>
                  </ul>
                </div>
              </section>

              {/* Types of Cookies */}
              <section
                id="types"
                className={activeSection === 'types' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.types.title')}
                </h2>
                <div className="space-y-4">
                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <h3 className="text-lg font-semibold text-blue-300">
                      {t('cookiePolicy.sections.types.essential')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('cookiePolicy.sections.types.essentialDesc')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
                    <h3 className="text-lg font-semibold text-green-300">
                      {t('cookiePolicy.sections.types.preference')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('cookiePolicy.sections.types.preferenceDesc')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                    <h3 className="text-lg font-semibold text-purple-300">
                      {t('cookiePolicy.sections.types.analytics')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('cookiePolicy.sections.types.analyticsDesc')}
                    </p>
                    <div className="mt-3 bg-purple-900/30 p-3 rounded-md">
                      <h4 className="text-sm font-semibold text-white">
                        Google Analytics
                      </h4>
                      <p className="text-gray-200 text-sm mt-1">
                        {t('cookiePolicy.sections.types.googleAnalyticsDetail')}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Managing Cookies */}
              <section
                id="managing"
                className={activeSection === 'managing' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.managing.title')}
                </h2>
                <p className="text-white mb-6">
                  {t('cookiePolicy.sections.managing.description')}
                </p>

                {/* New Cookie Consent Banner Information */}
                <div className="bg-blue-50/30 backdrop-blur-sm border-l-4 border-blue-500 p-4 mb-6">
                  <h3 className="font-semibold text-blue-200">
                    {t('cookiePolicy.sections.managing.consentBanner')}
                  </h3>
                  <p className="text-white mt-2">
                    {t('cookiePolicy.sections.managing.consentBannerDesc')}
                  </p>
                </div>

                <div className="bg-yellow-50/30 backdrop-blur-sm border-l-4 border-yellow-400 p-4">
                  <h3 className="font-semibold text-yellow-200">
                    {t('cookiePolicy.sections.managing.browserSettings')}
                  </h3>
                  <p className="text-white mt-2">
                    {t('cookiePolicy.sections.managing.browserSettingsDesc')}
                  </p>
                </div>
              </section>

              {/* Changes */}
              <section
                id="changes"
                className={activeSection === 'changes' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.changes.title')}
                </h2>
                <p className="text-white">
                  {t('cookiePolicy.sections.changes.description')}
                </p>
                <div className="flex items-center mt-6 bg-gray-50/30 backdrop-blur-sm p-4 rounded-lg border border-gray-200">
                  <div className="bg-blue-100/30 p-2 rounded-full mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-white text-sm">
                    {t('cookiePolicy.sections.changes.note')}
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section
                id="contact"
                className={activeSection === 'contact' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('cookiePolicy.sections.contact.title')}
                </h2>
                <p className="text-white mb-6">
                  {t('cookiePolicy.sections.contact.description')}
                </p>
                <a
                  href="mailto:contact@betzen.app"
                  className="inline-flex items-center bg-gradient-to-r from-purple-800 to-blue-700 text-white px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  style={{
                    background: 'linear-gradient(90deg, #5f2c82, #49a09d)',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  contact@betzen.app
                </a>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};
